import './SendToEisExecutor.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IEisAsyncSendStatusModel } from '@models/document/eis/IEisAsyncSendStatusModel';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { DocumentLoadEisLogService } from '@services/DocumentLoadEisLogService';
import {
    ISendToEisModel,
    ISendToEisPackageSize,
} from '@models/document/actionsExecution/ISendToEisModel';
import { ActionResultStatus } from '@models/actions/ActionResultStatus';
import { EisAsyncSendStatus } from '@models/document/eis/EisAsyncSendStatus';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import InputControl from '@srm/ui/controls/InputControl';
import Hint from '@srm/ui/hint';
import Button from '@srm/ui/button';
import Select from '@srm/ui/select';
import Tooltip from '@srm/ui/tooltip';
import { IOption } from '@srm/ui/types/IOption';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { ISendToEisExecutorProps } from './ISendToEisExecutorProps';
import { ISendToEisFormData } from './ISendToEisFormData';
import { API_URL } from '@/http';
import { auth } from '@/auth';
import * as signalR from '@microsoft/signalr';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';

const hubMethodName = 'eisSendCompleted';
const asyncSendStatusFallback = 'Документ обрабатывается в ЕИС…';
const allDocumentsOptionValue = '__all_documents__';

const formatSize = (size: number): string => {
    if (size < 1024) {
        return `${size.toLocaleString('ru-RU')} Б`;
    }

    if (size < 1024 * 1024) {
        const kilobytes = size / 1024;
        return `${kilobytes.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} КБ`;
    }

    const megabytes = size / (1024 * 1024);
    return `${megabytes.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} МБ`;
};

interface IPackageSizeCardProps {
    title: string;
    tooltip: string;
    size: number;
    total?: boolean;
}

const PackageSizeCard: React.FC<IPackageSizeCardProps> = ({ title, tooltip, size, total }) => (
    <div className={`send-to-eis-package-size__card${total ? ' send-to-eis-package-size__card--total' : ''}`}>
        <Tooltip openDelay={100} background="black" position="top" maxWidth="320px" label={<span>{tooltip}</span>}>
            <span className="send-to-eis-package-size__card-title">
                <span className="send-to-eis-package-size__card-label">{title}</span>
                <span className="send-to-eis-package-size__card-info" aria-hidden="true">
                    i
                </span>
            </span>
        </Tooltip>
        <strong>{formatSize(size)}</strong>
    </div>
);

const formatFilesCount = (count: number): string => {
    const lastTwoDigits = count % 100;
    const lastDigit = count % 10;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return `${count} файлов`;
    }

    if (lastDigit === 1) {
        return `${count} файл`;
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return `${count} файла`;
    }

    return `${count} файлов`;
};

interface IEisSendCompletedEvent {
    isSucceed: boolean;
    status: ActionResultStatus;
    message: string;
}

export const SendToEisExecutorModal: React.FC<ISendToEisExecutorProps> = (props: ISendToEisExecutorProps) => {
    // Подготовка (модель, расчёт размера пакета) и отправка — разные состояния: подготовку можно
    // бросить закрытием окна, отправку (до ответа ЕИС по SignalR) — нет.
    const [loading, setLoading] = useState<boolean>(true);
    const [sending, setSending] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [errorText, setErrorText] = useState<string>();
    const [successText, setSuccessText] = useState<string>();
    const [authRequired, SetAuthRequired] = useState<boolean>();
    const [packageSize, setPackageSize] = useState<ISendToEisPackageSize>();
    const [packageSizeLoading, setPackageSizeLoading] = useState<boolean>(false);
    const [selectedDocumentId, setSelectedDocumentId] = useState<string>(allDocumentsOptionValue);
    const formState = useForm<ISendToEisFormData>({
        mode: 'onBlur', // "onChange"
    });
    const hasUnsavedInput = useUnsavedFormInput(formState);
    const [asyncSendExecuteFlag, setAsyncSendExecuteFlag] = useState<boolean>(false);
    const [asyncSendFlag, setAsyncSendFlag] = useState<boolean>(false);
    const [asyncSendModel, setAsyncSendModel] = useState<IEisAsyncSendStatusModel>();

    const service = new DocumentExecutionService(props.docId);
    const eisService = new DocumentLoadEisLogService();

    const documentOptions = useMemo<IOption[]>(() => {
        if (!packageSize) {
            return [];
        }

        const summaries = new Map<string, { count: number; size: number }>();
        packageSize.files.forEach((file) => {
            const summary = summaries.get(file.documentId) ?? { count: 0, size: 0 };
            summary.count += 1;
            summary.size += file.size;
            summaries.set(file.documentId, summary);
        });

        return [
            {
                value: allDocumentsOptionValue,
                label: `Все документы (${packageSize.documentIds.length})`,
            },
            ...packageSize.documentIds.map((documentId) => {
                const summary = summaries.get(documentId) ?? { count: 0, size: 0 };
                return {
                    value: documentId,
                    label: `Документ ${documentId} · ${formatFilesCount(summary.count)} · ${formatSize(summary.size)}`,
                };
            }),
        ];
    }, [packageSize]);

    const selectedDocumentOption =
        documentOptions.find((option) => option.value === selectedDocumentId) ?? documentOptions[0];

    const visibleFiles = useMemo(() => {
        if (!packageSize) {
            return [];
        }

        return selectedDocumentId === allDocumentsOptionValue
            ? packageSize.files
            : packageSize.files.filter((file) => file.documentId === selectedDocumentId);
    }, [packageSize, selectedDocumentId]);

    const visibleFilesSize = useMemo(
        () => visibleFiles.reduce((totalSize, file) => totalSize + file.size, 0),
        [visibleFiles],
    );

    useEffect(() => {
        if (
            packageSize &&
            selectedDocumentId !== allDocumentsOptionValue &&
            !packageSize.documentIds.includes(selectedDocumentId)
        ) {
            setSelectedDocumentId(allDocumentsOptionValue);
        }
    }, [packageSize, selectedDocumentId]);

    const hubConn = useMemo(() => {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(`${API_URL}/hubs/autotask`, {
                accessTokenFactory: () => auth.pageAccessToken() ?? '',
            })
            .build();
        const pendingResults = new Map<string, IEisSendCompletedEvent>();
        let eisSendingGuidAtStart = '';
        let isWaitingForResult = false;
        let isDisposed = false;

        const resetWaitingState = () => {
            isWaitingForResult = false;
            eisSendingGuidAtStart = '';
            pendingResults.clear();
        };

        const applyResult = ({ isSucceed, status, message }: IEisSendCompletedEvent) => {
            resetWaitingState();
            void connection.stop();

            if (isSucceed) {
                setSuccessText(message);
                setSuccess(true);
                if (status === ActionResultStatus.Executing) {
                    setAsyncSendFlag(true);
                    setAsyncSendExecuteFlag(true);
                }
            } else {
                setErrorText(message);
            }

            setSending(false);
        };

        const signalrIdHandler = (
            eisSendingGuid: string,
            isSucceed: boolean,
            status: ActionResultStatus,
            message: string,
        ) => {
            if (!isWaitingForResult) {
                return;
            }

            const result = { isSucceed, status, message };
            if (!eisSendingGuidAtStart) {
                pendingResults.set(eisSendingGuid, result);
                return;
            }

            if (eisSendingGuid === eisSendingGuidAtStart) {
                applyResult(result);
            }
        };

        connection.on(hubMethodName, signalrIdHandler);
        connection.onclose((error) => {
            if (!isWaitingForResult || isDisposed) {
                return;
            }

            resetWaitingState();
            setErrorText(
                `Соединение с сервером прервано. Результат отправки в ЕИС неизвестен. Проверьте состояние документа перед повторной отправкой.${error ? ` Причина: ${error.message}` : ''}`,
            );
            setSending(false);
        });

        return {
            connection,
            beginWaiting: () => {
                isWaitingForResult = true;
                eisSendingGuidAtStart = '';
                pendingResults.clear();
            },
            setTaskGuid: (taskGuid: string) => {
                if (!isWaitingForResult) {
                    return;
                }

                eisSendingGuidAtStart = taskGuid;
                const pendingResult = pendingResults.get(taskGuid);
                pendingResults.clear();
                if (pendingResult) {
                    applyResult(pendingResult);
                }
            },
            cancelWaiting: () => {
                resetWaitingState();
                void connection.stop();
            },
            dispose: () => {
                isDisposed = true;
                resetWaitingState();
                connection.off(hubMethodName, signalrIdHandler);
                void connection.stop();
            },
        };
    }, []);

    useEffect(
        () => () => {
            hubConn.dispose();
        },
        [hubConn],
    );

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        // Модель данных
        setLoading(true);
        service
            .getSendToEisModel(props.actionKey)
            .then((result) => {
                if (ignore) return;
                if (result.data) {
                    SetAuthRequired(result.data.authRequired);
                    setPackageSize(result.data.packageSize);
                    formState.reset({
                        login: result.data.login,
                        password: result.data.password,
                    });
                }
            })
            .catch((error) => {
                if (!ignore) setErrorText(error);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, []);

    const onSubmit = (form: ISendToEisFormData) => {
        // Отправить данные
        setSending(true);
        setErrorText('');
        setSuccessText('');
        const sendToEisData: ISendToEisModel = {
            login: form.login,
            password: form.password,
        };

        hubConn.beginWaiting();
        return hubConn.connection
            .start()
            .then(() => service.sendToEis(props.actionKey, sendToEisData))
            .then((dto) => {
                // GUID из HTTP-ответа — идентификатор фоновой задачи. Итоговое сообщение приходит по SignalR.
                hubConn.setTaskGuid(dto.data.message);
            })
            .catch((error) => {
                hubConn.cancelWaiting();
                setErrorText(error);
                setSending(false);
            });
    };

    const calculateExactPackageSize = async () => {
        const form = formState.getValues();
        setPackageSizeLoading(true);
        setErrorText('');

        try {
            const result = await service.calculateSendToEisPackageSize(props.actionKey, {
                login: form.login,
                password: form.password,
            });
            setPackageSize(result.data);
        } catch (error) {
            setErrorText(error as string);
        } finally {
            setPackageSizeLoading(false);
        }
    };

    let interval: number;

    const processEisAsyncSendStatus = () => {
        eisService.getEisAsyncSendStatus(props.docId).then((response) => {
            if (
                response.data.statusEnum === EisAsyncSendStatus.Success ||
                response.data.statusEnum === EisAsyncSendStatus.Error
            ) {
                setAsyncSendExecuteFlag(false);
            }
            setAsyncSendModel(response.data);
        });
    };

    const startEisAsyncSendStatusMonitoring = () => {
        interval = window.setInterval(() => {
            processEisAsyncSendStatus();
        }, 1000);
    };

    const stopEisAsyncSendStatusMonitoring = () => {
        clearInterval(interval);
    };

    useEffect(() => {
        if (asyncSendExecuteFlag) {
            startEisAsyncSendStatusMonitoring();
        } else {
            stopEisAsyncSendStatusMonitoring();
        }
        return () => stopEisAsyncSendStatusMonitoring();
    }, [asyncSendExecuteFlag]);

    const modalContainerProps: IModalContainerProps = {
        header: props.message,
        size: props.modalSize,
        showOkButton: !success,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        onCancel: () => {
            props.completeHandler && props.completeHandler(success);
        },
        errorText: errorText,
        successText: successText,
        formMethods: formState,
        onSubmit: onSubmit,
        okButtonDisabled: packageSizeLoading,
        loading: loading || sending,
        closeDisabled: sending,
        hasUnsavedInput: () => !success && hasUnsavedInput(),
    };

    return (
        <ModalContainer {...modalContainerProps}>
            {!success && authRequired && (
                <InputControl
                    label="Логин"
                    name="login"
                    autoComplete="off"
                    required={authRequired}
                    rules={authRequired ? requiredTextRules() : undefined}
                    control={formState.control}
                />
            )}

            {!success && authRequired && (
                <InputControl
                    label="Пароль"
                    name="password"
                    autoComplete="off"
                    type="password"
                    required={authRequired}
                    rules={authRequired ? requiredTextRules() : undefined}
                    control={formState.control}
                />
            )}

            {!success && packageSize && (
                <section className="send-to-eis-package-size">
                    <div className="send-to-eis-package-size__notice">
                        <Hint
                            icon="info"
                            title="В расчёт включены только выбранные файлы с признаком «Публикуется»."
                            tone="info"
                            maxWidth="100%"
                        />
                    </div>
                    <div className="send-to-eis-package-size__header">
                        <div>
                            <h3>Размер пакета</h3>
                            <span>{packageSize.isExact ? 'Точный расчет' : 'Предварительный расчет'}</span>
                        </div>
                    </div>

                    <div className="send-to-eis-package-size__cards">
                        <PackageSizeCard
                            title="Итоговый размер пакета"
                            tooltip="Размер XML-пакета, который будет отправлен в ЕИС. Файлы включаются в него в кодировке Base64, поэтому пакет больше исходных файлов."
                            size={packageSize.totalSize}
                            total
                        />
                        <PackageSizeCard
                            title="Размер файлов"
                            tooltip="Общий размер выбранных файлов карточки документа с признаком «Публикуется» до их добавления в пакет ЕИС."
                            size={packageSize.filesSize}
                        />
                        <PackageSizeCard
                            title="Размер данных документа"
                            tooltip="Размер XML с данными документа без содержимого прикреплённых файлов."
                            size={packageSize.xmlSize}
                        />
                    </div>

                    <div className="send-to-eis-package-size__calculation-result">
                        <div>
                            <div className="send-to-eis-package-size__difference">
                                Увеличение размера после упаковки:{' '}
                                <strong>
                                    {formatSize(
                                        Math.max(0, packageSize.totalSize - packageSize.filesSize - packageSize.xmlSize),
                                    )}
                                </strong>
                            </div>
                            {packageSize.isExact && (
                                <div className="send-to-eis-package-size__exact-status">
                                    <span aria-hidden="true">✓</span>
                                    Точный размер рассчитан
                                </div>
                            )}
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            loading={packageSizeLoading}
                            disabled={packageSizeLoading}
                            onClick={calculateExactPackageSize}
                        >
                            {packageSize.isExact ? 'Рассчитать повторно' : 'Рассчитать точный размер'}
                        </Button>
                    </div>

                    <h4 className="send-to-eis-package-size__files-title">Файлы для публикации</h4>
                    <div className="send-to-eis-package-size__documents">
                        <span>ИД документов:</span>{' '}
                        {packageSize.documentIds.map((documentId, index) => (
                            <React.Fragment key={`${documentId}-${index}`}>
                                {index > 0 && ', '}
                                <a href={`/document/${documentId}`} target="_blank" rel="noopener noreferrer">
                                    {documentId}
                                </a>
                            </React.Fragment>
                        ))}
                    </div>
                    {packageSize.documentIds.length > 1 && (
                        <div className="send-to-eis-package-size__document-filter">
                            <span>Показать файлы:</span>
                            <Select
                                options={documentOptions}
                                values={selectedDocumentOption ? [selectedDocumentOption] : []}
                                placeholder="Выберите документ"
                                onChange={(options) =>
                                    setSelectedDocumentId(options[0]?.value?.toString() ?? allDocumentsOptionValue)
                                }
                            />
                        </div>
                    )}
                    <div className="send-to-eis-package-size__table-wrap">
                        <table className="send-to-eis-package-size__table">
                            <thead>
                                <tr>
                                    <th>Файл</th>
                                    <th>Вид файла</th>
                                    <th>Категория файла</th>
                                    <th>Карточка</th>
                                    <th>Размер</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleFiles.length ? (
                                    visibleFiles.map((file, index) => (
                                        <tr key={`${file.documentId}-${file.fileName}-${index}`}>
                                            <td>{file.fileName}</td>
                                            <td>{file.docType || '—'}</td>
                                            <td>{file.category || '—'}</td>
                                            <td>{file.documentId}</td>
                                            <td>{formatSize(file.size)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5}>Файлы для публикации не выбраны</td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={4}>Итого: {formatFilesCount(visibleFiles.length)}</td>
                                    <td>{formatSize(visibleFilesSize)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </section>
            )}

            {asyncSendFlag && asyncSendModel?.statusEnum === EisAsyncSendStatus.InProgress && (
                <Hint
                    title={asyncSendModel.statusText?.trim() || asyncSendStatusFallback}
                    tone="info"
                    maxWidth="100%"
                />
            )}
            {asyncSendFlag && asyncSendModel?.statusEnum === EisAsyncSendStatus.Error && (
                <Hint role="alert" title={`${asyncSendModel?.statusText}`} tone="danger" maxWidth="100%" />
            )}
            {asyncSendFlag && asyncSendModel?.statusEnum === EisAsyncSendStatus.Success && (
                <Hint role="alert" title={`${asyncSendModel?.statusText}`} tone="success" maxWidth="100%" />
            )}
            {asyncSendFlag && asyncSendModel?.statusEnum === EisAsyncSendStatus.Queued && (
                <Hint
                    title={asyncSendModel.statusText?.trim() || asyncSendStatusFallback}
                    tone="info"
                    maxWidth="100%"
                />
            )}
        </ModalContainer>
    );
};
